import { createClient } from "@supabase/supabase-js";

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

// NOTE: All Stripe operations now go through the backend via src/apiClient.js
// (/api/v2/stripe/*). The Stripe SECRET key must never be referenced in this
// browser bundle - see secure-stripe-endpoints.md.

export const checkAuth = async () => {
  try {
    const sessionStr = localStorage.getItem('session');
    if (!sessionStr) {
      return { authenticated: false };
    }

    const session = JSON.parse(sessionStr);
    
    if (session.expires_at && session.expires_at < Math.floor(Date.now() / 1000)) {
      localStorage.removeItem('session');
      return { authenticated: false };
    }

    const response = await fetch(`${API_URL}/api/verify-session`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      localStorage.removeItem('session');
      return { authenticated: false };
    }

    const data = await response.json();
    
    return {
      user: data.user,
      role: data.user.role,
      authenticated: true,
      vendor_id: data.id && data.type === 'vendor' ? data.id : null,
      org_id: data.id && data.type === 'organization' ? data.id : null
    };
  } catch (error) {
    console.error('Error checking authentication:', error);
    return { authenticated: false };
  }
};

export const createUserProfile = async (userId, data) => {
  try {
    const session = localStorage.getItem('session');
    const sessionObj = session ? JSON.parse(session) : null;
    const headers = {
      'Content-Type': 'application/json',
    };
    
    if (sessionObj && sessionObj.access_token) {
      headers['Authorization'] = `Bearer ${sessionObj.access_token}`;
    }
    
    const response = await fetch(`${API_URL}/api/users/profile`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        id: userId,
        role: "customer",
        ...data,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      return { error: new Error(errorData.message || 'Failed to create user profile') };
    }

    return { error: null };
  } catch (error) {
    return { error };
  }
};

export const createVendorProfile = async (userId, userData) => {
  try {
    const session = localStorage.getItem('session');
    const sessionObj = session ? JSON.parse(session) : null;
    const headers = {
      'Content-Type': 'application/json',
    };
    
    if (sessionObj && sessionObj.access_token) {
      headers['Authorization'] = `Bearer ${sessionObj.access_token}`;
    }
    
    const response = await fetch(`${API_URL}/api/vendors/profile`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        user_id: userId,
        email: userData.email,
        phone_number: userData.contact_phone,
        first_name: userData.first_name,
        last_name: userData.last_name,
        company_name: userData.company_name,
        contact_email: userData.email,
        contact_phone: userData.contact_phone,
        address: userData.address,
        type: userData.type,
        mascotname: userData.mascotname,
        short_name: userData.short_name,
        secondary_color: "#1c4ed8",
        primary_color: "#1f3a8a",
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      return { error: new Error(errorData.message || 'Failed to create vendor profile') };
    }

    return { error: null };
  } catch (error) {
    return { error };
  }
};

export const createOrganizationProfile = async (userId, userData) => {
  try {
    const session = localStorage.getItem('session');
    const sessionObj = session ? JSON.parse(session) : null;
    const headers = {
      'Content-Type': 'application/json',
    };
    
    if (sessionObj && sessionObj.access_token) {
      headers['Authorization'] = `Bearer ${sessionObj.access_token}`;
    }
    
    const response = await fetch(`${API_URL}/api/organizations/profile`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        user_id: userId,
        email: userData.email,
        phone_number: userData.contact_phone,
        first_name: userData.first_name,
        last_name: userData.last_name,
        company_name: userData.company_name,
        contact_email: userData.email,
        contact_phone: userData.contact_phone,
        address: userData.address,
        type: userData.type,
        primary_color: "#1f3a8a",
        secondary_color: "#1c4ed8",
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      return { error: new Error(errorData.message || 'Failed to create organization profile') };
    }

    return { error: null };
  } catch (error) {
    return { error };
  }
};

export const fetchEvents = async () => {
  try {
    const session = localStorage.getItem('session');
    const sessionObj = session ? JSON.parse(session) : null;
    const headers = {
      'Content-Type': 'application/json',
    };
    
    if (sessionObj && sessionObj.access_token) {
      headers['Authorization'] = `Bearer ${sessionObj.access_token}`;
    }
    
    const response = await fetch(`${API_URL}/api/events`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch events: ${response.statusText}`);
    }

    const data = await response.json();
    return { data: data.events, error: null };
  } catch (error) {
    console.error("Error fetching events:", error);
    return { data: null, error };
  }
};

export const fetchVendors = async () => {
  try {
    const session = localStorage.getItem('session');
    const sessionObj = session ? JSON.parse(session) : null;
    const headers = {
      'Content-Type': 'application/json',
    };
    
    if (sessionObj && sessionObj.access_token) {
      headers['Authorization'] = `Bearer ${sessionObj.access_token}`;
    }
    
    const response = await fetch(`${API_URL}/api/vendors`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch vendors: ${response.statusText}`);
    }

    const data = await response.json();
    return { data: data.vendors, error: null };
  } catch (error) {
    console.error("Error fetching vendors:", error);
    return { data: null, error };
  }
};

export const fetchVendorById = async (vendorId) => {
  try {
    const session = localStorage.getItem('session');
    const sessionObj = session ? JSON.parse(session) : null;
    const headers = {
      'Content-Type': 'application/json',
    };
    
    if (sessionObj && sessionObj.access_token) {
      headers['Authorization'] = `Bearer ${sessionObj.access_token}`;
    }
    
    const response = await fetch(`${API_URL}/api/vendors/${vendorId}`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch vendor: ${response.statusText}`);
    }

    const data = await response.json();
    return { data: data.vendor, error: null };
  } catch (error) {
    console.error("Error fetching vendor:", error);
    return { data: null, error };
  }
};

export const fetchEventsByVendorId = async (vendorId, includePast = false) => {
  try {
    const session = localStorage.getItem('session');
    const sessionObj = session ? JSON.parse(session) : null;
    const headers = {
      'Content-Type': 'application/json',
    };
    
    if (sessionObj && sessionObj.access_token) {
      headers['Authorization'] = `Bearer ${sessionObj.access_token}`;
    }
    
    // Build URL with query parameter for past events - backend now supports this!
    const url = includePast 
      ? `${API_URL}/api/public/vendors/${vendorId}/events?past=true`
      : `${API_URL}/api/public/vendors/${vendorId}/events`;
    
    const response = await fetch(url, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch events: ${response.statusText} (${response.status})`);
    }

    const data = await response.json();
    
    // Backend now handles filtering and sorting, so we can directly return the data
    return { data: data.events, error: null };
  } catch (error) {
    console.error("Error fetching events:", error);
    return { data: null, error };
  }
};

export const fetchEventById = async (eventId) => {
  try {
    const session = localStorage.getItem('session');
    const sessionObj = session ? JSON.parse(session) : null;
    const headers = {
      'Content-Type': 'application/json',
    };
    
    if (sessionObj && sessionObj.access_token) {
      headers['Authorization'] = `Bearer ${sessionObj.access_token}`;
    }
    
    const response = await fetch(`${API_URL}/api/events/${eventId}`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch event: ${response.statusText}`);
    }

    const data = await response.json();
    return { data: data.event, error: null };
  } catch (error) {
    console.error("Error fetching event:", error);
    return { data: null, error };
  }
};

export const fetchTicketsByEventId = async (eventId) => {
  try {
    const session = localStorage.getItem('session');
    const sessionObj = session ? JSON.parse(session) : null;
    const headers = {
      'Content-Type': 'application/json',
    };
    
    if (sessionObj && sessionObj.access_token) {
      headers['Authorization'] = `Bearer ${sessionObj.access_token}`;
    }
    
    const response = await fetch(`${API_URL}/api/events/${eventId}/tickets`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch tickets: ${response.statusText}`);
    }

    const data = await response.json();
    return { data: data.tickets, error: null };
  } catch (error) {
    console.error("Error fetching tickets:", error);
    return { data: null, error };
  }
};

export const deleteEvent = async (eventId) => {
  try {
    // Get the auth token from localStorage
    const sessionStr = localStorage.getItem('session');
    if (!sessionStr) {
      throw new Error('No active session found');
    }
    
    const session = JSON.parse(sessionStr);
    
    // Make the API request
    const response = await fetch(`${API_URL}/api/events/${eventId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      return { error: errorData };
    }
    
    return { error: null };
  } catch (error) {
    console.error('Error deleting event:', error);
    return { error };
  }
};

export const deleteTicket = async (ticketId) => {
  try {
    // Get the auth token from localStorage
    const sessionStr = localStorage.getItem('session');
    if (!sessionStr) {
      throw new Error('No active session found');
    }
    
    const session = JSON.parse(sessionStr);
    
    // Make the API request
    const response = await fetch(`${API_URL}/api/tickets/${ticketId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      return { error: errorData };
    }
    
    return { error: null };
  } catch (error) {
    console.error('Error deleting ticket:', error);
    return { error };
  }
};

export const recordTicketSale = async (ticketSales) => {
  try {
    const session = localStorage.getItem('session');
    const sessionObj = session ? JSON.parse(session) : null;
    const headers = {
      'Content-Type': 'application/json',
    };
    
    if (sessionObj && sessionObj.access_token) {
      headers['Authorization'] = `Bearer ${sessionObj.access_token}`;
    }
    
    const response = await fetch(`${API_URL}/api/tickets/sales`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ ticketSales }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to record ticket sales');
    }

    const data = await response.json();
    return { data: data.tickets, error: null };
  } catch (error) {
    console.error("Error recording ticket sale:", error);
    return { data: null, error };
  }
};

export const fetchUserTickets = async (userEmail) => {
  try {
    const session = localStorage.getItem('session');
    const sessionObj = session ? JSON.parse(session) : null;
    const headers = {
      'Content-Type': 'application/json',
    };
    
    if (sessionObj && sessionObj.access_token) {
      headers['Authorization'] = `Bearer ${sessionObj.access_token}`;
    }
    
    const response = await fetch(`${API_URL}/api/tickets/user/${encodeURIComponent(userEmail)}`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch user tickets: ${response.statusText}`);
    }

    const data = await response.json();
    return { data: data.tickets, error: null };
  } catch (error) {
    console.error("Error fetching user tickets:", error);
    return { data: null, error };
  }
};

export const fetchEventTypes = async () => {
  try {
    const sessionStr = localStorage.getItem('session');
    if (!sessionStr) {
      throw new Error('No active session found');
    }
    
    const session = JSON.parse(sessionStr);
    
    const response = await fetch(`${API_URL}/api/v2/event-types`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      throw new Error(`Failed to fetch event types: ${response.statusText}`);
    }
    
    const data = await response.json();
    return { data: data.eventTypes, error: null };
  } catch (error) {
    console.error('Error fetching event types:', error);
    return { data: null, error };
  }
};

export const fetchDashboardStats = async (vendorId, selectedMonth, selectedEventType = null) => {
  try {
    // Get the auth token from localStorage
    const sessionStr = localStorage.getItem('session');
    if (!sessionStr) {
      throw new Error('No active session found');
    }
    
    const session = JSON.parse(sessionStr);
    
    // Build the query parameters
    const params = new URLSearchParams();
    
    // Format the month parameter as ISO string (first day of month)
    const monthStr = selectedMonth.toISOString().split('T')[0];
    params.append('month', monthStr);
    
    if (selectedEventType) {
      params.append('eventType', selectedEventType);
    }
    
    // Make the API request
    const response = await fetch(`${API_URL}/api/v2/vendors/${vendorId}/dashboard-stats?${params.toString()}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      throw new Error(`Failed to fetch dashboard stats: ${response.statusText}`);
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching dashboard stats:', error);
    throw error;
  }
};

export const recordPageView = async (vendorId) => {
  try {
    // Generate a session ID if one doesn't exist
    let sessionId = localStorage.getItem('session_id');
    if (!sessionId) {
      sessionId = Math.random().toString(36).substring(2) + Date.now().toString(36);
      localStorage.setItem('session_id', sessionId);
    }

    // Get IP address from ipify API
    const ipResponse = await fetch('https://api.ipify.org?format=json');
    const { ip } = await ipResponse.json();

    // Get user agent
    const userAgent = navigator.userAgent;

    console.log('Recording page view:', {
      vendorId,
      sessionId,
      userAgent,
      ip
    });

    // Insert page view with all details
    const { data, error } = await supabase
      .from('page_views')
      .insert({
        vendor_id: vendorId,
        session_id: sessionId,
        user_agent: userAgent,
        ip_address: ip
      })
      .select();

    if (error) {
      console.error('Supabase error:', error);
      throw error;
    }

    //console.log('Page view recorded:', data);
    return { data, error: null };
  } catch (error) {
    console.error('Error recording page view:', error);
    return { data: null, error };
  }
};

// Update the fetchVendorEventTypes function to use the API instead of direct Supabase call
export const fetchVendorEventTypes = async (vendorId) => {
  try {
    // Get the auth token from localStorage
    const sessionStr = localStorage.getItem('session');
    if (!sessionStr) {
      throw new Error('No active session found');
    }
    
    const session = JSON.parse(sessionStr);
    
    // Make the API request
    const response = await fetch(`${API_URL}/api/v2/vendors/${vendorId}/event-types`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      throw new Error(`Failed to fetch event types: ${response.statusText}`);
    }
    
    const data = await response.json();
    return { data: data.eventTypes };
  } catch (error) {
    console.error('Error fetching event types:', error);
    throw error;
  }
};

export const fetchOrganizationData = async (organizationId) => {
  try {
    const sessionStr = localStorage.getItem('session');
    
    if (!sessionStr) {
      throw new Error('No active session found');
    }
    
    const session = JSON.parse(sessionStr);
    const response = await fetch(`${API_URL}/api/organizations/${organizationId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch organization data: ${response.statusText}`);
    }

    const data = await response.json();
    //console.log('Organization data:', data);
    return data;
  } catch (error) {
    console.error('Error fetching organization data:', error);
    throw error;
  }
};

export const updateOrganizationData = async (organizationId, updateData) => {
  try {
    const { data, error } = await supabase
      .from('organizations')
      .update(updateData)
      .eq('id', organizationId)
      .select()
      .single();

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('Error updating organization:', error);
    return { data: null, error };
  }
};

export const fetchVendorMonthlyReport = async (vendorId) => {
  try {
    // Get start of current month
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
    
    // Get vendor info
    const { data: vendorData, error: vendorError } = await supabase
      .from('vendors')
      .select('company_name, contact_email, contact_phone')
      .eq('id', vendorId)
      .single();
    
    if (vendorError) throw vendorError;
    
    // Get events for this month
    const { data: eventsData, error: eventsError } = await supabase
      .from('events')
      .select('*')
      .eq('vendor_id', vendorId)
      .eq('isDeleted', false)
      .gte('created_at', startOfMonth);
      
    if (eventsError) throw eventsError;
    
    // Get tickets sold for this month
    const { data: ticketsData, error: ticketsError } = await supabase
      .from('tickets_sold')
      .select('*')
      .eq('vendor_id', vendorId)
      .gte('purchase_date', startOfMonth);
      
    if (ticketsError) throw ticketsError;
    
    // Calculate metrics
    const totalRevenue = ticketsData.reduce((sum, ticket) => sum + (ticket.price_paid || 0), 0);
    const ticketCount = ticketsData.length;
    
    // Calculate metrics by event type
    const eventTypes = [...new Set(eventsData.map(event => event.event_type))];
    const eventTypeMetrics = eventTypes.map(type => {
      const eventsOfType = eventsData.filter(event => event.event_type === type);
      const eventIds = eventsOfType.map(event => event.id);
      const ticketsForType = ticketsData.filter(ticket => eventIds.includes(ticket.event_id));
      const revenueForType = ticketsForType.reduce((sum, ticket) => sum + (ticket.price_paid || 0), 0);
      
      return {
        type,
        count: eventsOfType.length,
        tickets: ticketsForType.length,
        revenue: revenueForType
      };
    });
    
    // Calculate top performing events
    const eventTicketCounts = {};
    const eventRevenue = {};
    
    ticketsData.forEach(ticket => {
      if (!eventTicketCounts[ticket.event_id]) {
        eventTicketCounts[ticket.event_id] = 0;
        eventRevenue[ticket.event_id] = 0;
      }
      eventTicketCounts[ticket.event_id]++;
      eventRevenue[ticket.event_id] += ticket.price_paid || 0;
    });
    
    // Match with event names
    const topEvents = Object.keys(eventTicketCounts)
      .map(eventId => {
        const event = eventsData.find(e => e.id === eventId);
        return {
          eventId,
          name: event ? event.title : 'Unknown Event',
          ticketsSold: eventTicketCounts[eventId],
          revenue: eventRevenue[eventId]
        };
      })
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 5);
    
    // Weekly sales tracking
    const weeklySales = {};
    ticketsData.forEach(ticket => {
      const purchaseDate = new Date(ticket.purchase_date);
      const weekNumber = Math.floor((purchaseDate - new Date(startOfMonth)) / (7 * 24 * 60 * 60 * 1000));
      const weekKey = `Week ${weekNumber + 1}`;
      
      if (!weeklySales[weekKey]) {
        weeklySales[weekKey] = {
          count: 0,
          revenue: 0
        };
      }
      
      weeklySales[weekKey].count++;
      weeklySales[weekKey].revenue += ticket.price_paid || 0;
    });
    
    const weeklySalesArray = Object.entries(weeklySales).map(([week, data]) => ({
      week,
      ticketsSold: data.count,
      revenue: data.revenue
    }));
    
    return {
      data: {
        vendor: vendorData,
        summary: {
          totalRevenue,
          ticketCount,
          eventCount: eventsData.length,
          avgTicketPrice: ticketCount > 0 ? totalRevenue / ticketCount : 0,
          reportPeriod: {
            start: startOfMonth,
            end: new Date().toISOString()
          }
        },
        byEventType: eventTypeMetrics,
        topEvents,
        weeklySales: weeklySalesArray
      },
      error: null
    };
  } catch (error) {
    console.error('Error fetching vendor report:', error);
    return { data: null, error };
  }
};

// Add these API endpoint functions for EventsTab

// Function to fetch events for a vendor - updated to use API
export const fetchVendorEvents = async (vendorId, includePast = false) => {
  try {
    // Get the auth token from localStorage
    const sessionStr = localStorage.getItem('session');
    if (!sessionStr) {
      throw new Error('No active session found');
    }
    
    const session = JSON.parse(sessionStr);
    
    // Build URL with query parameter if requesting past events
    const url = includePast 
      ? `${API_URL}/api/vendors/${vendorId}/events?past=true`
      : `${API_URL}/api/vendors/${vendorId}/events`;
    
    // Make the API request
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      throw new Error(`Failed to fetch events: ${response.statusText}`);
    }
    
    const data = await response.json();
    return { data: data.events, error: null };
  } catch (error) {
    console.error('Error fetching vendor events:', error);
    return { data: null, error };
  }
};

// Function to create an event via API
export const createEvent = async (vendorId, eventData) => {
  try {
    // Get the auth token from localStorage
    const sessionStr = localStorage.getItem('session');
    if (!sessionStr) {
      throw new Error('No active session found');
    }
    
    const session = JSON.parse(sessionStr);
    
    // Make the API request
    const response = await fetch(`${API_URL}/api/vendors/${vendorId}/events`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(eventData),
    });
    
    if (!response.ok) {
      throw new Error(`Failed to create event: ${response.statusText}`);
    }
    
    const data = await response.json();
    return { data: data.event, error: null };
  } catch (error) {
    console.error('Error creating event:', error);
    return { data: null, error };
  }
};

// Function to update an event via API
export const updateEvent = async (vendorId, eventId, eventData) => {
  try {
    // Get the auth token from localStorage
    const sessionStr = localStorage.getItem('session');
    if (!sessionStr) {
      throw new Error('No active session found');
    }
    
    const session = JSON.parse(sessionStr);
    
    // Make the API request
    const response = await fetch(`${API_URL}/api/vendors/${vendorId}/events/${eventId}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(eventData),
    });
    
    if (!response.ok) {
      throw new Error(`Failed to update event: ${response.statusText}`);
    }
    
    const data = await response.json();
    return { data: data.event, error: null };
  } catch (error) {
    console.error('Error updating event:', error);
    return { data: null, error };
  }
};

// Function to cancel an event via API
export const cancelEvent = async (vendorId, eventId) => {
  try {
    // Get the auth token from localStorage
    const sessionStr = localStorage.getItem('session');
    if (!sessionStr) {
      throw new Error('No active session found');
    }
    
    const session = JSON.parse(sessionStr);
    
    // Make the API request
    const response = await fetch(`${API_URL}/api/vendors/${vendorId}/events/${eventId}/cancel`, {
      method: 'PATCH',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `Failed to cancel event: ${response.statusText}`);
    }
    
    const data = await response.json();
    return { data: data.event, error: null };
  } catch (error) {
    console.error('Error cancelling event:', error);
    return { data: null, error };
  }
};

// Function to fetch tickets for an event via API
export const fetchEventTickets = async (eventId) => {
  try {
    // Get the auth token from localStorage
    const sessionStr = localStorage.getItem('session');
    if (!sessionStr) {
      throw new Error('No active session found');
    }
    
    const session = JSON.parse(sessionStr);
    
    // Make the API request
    const response = await fetch(`${API_URL}/api/events/${eventId}/tickets`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      throw new Error(`Failed to fetch tickets: ${response.statusText}`);
    }
    
    const data = await response.json();
    return { data: data.tickets, error: null };
  } catch (error) {
    console.error('Error fetching event tickets:', error);
    return { data: null, error };
  }
};

// Function to create a ticket via API
export const createTicket = async (eventId, ticketData) => {
  try {
    // Get the auth token from localStorage
    const sessionStr = localStorage.getItem('session');
    if (!sessionStr) {
      throw new Error('No active session found');
    }
    
    const session = JSON.parse(sessionStr);
    
    //console.log(`Creating ticket for event ${eventId} with data:`, ticketData);
    
    // Make the API request
    const response = await fetch(`${API_URL}/api/events/${eventId}/tickets`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(ticketData),
    });
    
    const responseData = await response.json();
    //console.log('Ticket API response:', responseData);
    
    if (!response.ok) {
      throw new Error(`Failed to create ticket: ${responseData.error || response.statusText}`);
    }
    
    return { data: responseData.ticket, error: null };
  } catch (error) {
    console.error('Error creating ticket:', error);
    return { data: null, error };
  }
};

// Function to update a ticket via API
export const updateTicket = async (ticketId, ticketData) => {
  try {
    // Get the auth token from localStorage
    const sessionStr = localStorage.getItem('session');
    if (!sessionStr) {
      throw new Error('No active session found');
    }
    
    const session = JSON.parse(sessionStr);
    
    // Make the API request
    const response = await fetch(`${API_URL}/api/tickets/${ticketId}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(ticketData),
    });
    
    if (!response.ok) {
      throw new Error(`Failed to update ticket: ${response.statusText}`);
    }
    
    const data = await response.json();
    return { data: data.ticket, error: null };
  } catch (error) {
    console.error('Error updating ticket:', error);
    return { data: null, error };
  }
};

// Function to count tickets sold for an event
export const countTicketsSold = async (eventId) => {
  try {
    // Get the auth token from localStorage
    const sessionStr = localStorage.getItem('session');
    if (!sessionStr) {
      throw new Error('No active session found');
    }
    
    const session = JSON.parse(sessionStr);
    
    // Make the API request
    const response = await fetch(`${API_URL}/api/events/${eventId}/tickets-sold/count`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      throw new Error(`Failed to count tickets sold: ${response.statusText}`);
    }
    
    const data = await response.json();
    return { count: data.count, error: null };
  } catch (error) {
    console.error('Error counting tickets sold:', error);
    return { count: null, error };
  }
};
